/* ============================================================
   Sensibo Partner Application Form
   Multi-step wizard with HubSpot Forms API v3 submission.

   Config is read from a <script type="application/json"
   id="partner-form-config-{section.id}"> block emitted by
   sections/partner-form.liquid.
   ============================================================ */

(function () {
  'use strict';

  class PartnerForm {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.form = root.querySelector('.partner-form__form');
      this.steps = Array.from(root.querySelectorAll('.partner-form__step'));
      this.progressBar = root.querySelector('[data-progress-bar]') || root.querySelector('.partner-form__progress-bar');
      this.stepCurrentEl = root.querySelector('[data-step-current]');
      this.stepTotalEl = root.querySelector('[data-step-total]');
      this.stepLabelEl = root.querySelector('[data-step-label]');
      this.submitBtn = root.querySelector('[data-action="submit"]');

      const configEl = document.getElementById('partner-form-config-' + this.sectionId);
      this.config = configEl ? JSON.parse(configEl.textContent) : {};

      this.currentIdx = 0;
      this.visitedHistory = [0];

      this.bindEvents();
      this.showStep(0);
    }

    bindEvents() {
      this.form.addEventListener('click', (e) => {
        const action = e.target.closest('[data-action]')?.dataset.action;
        if (action === 'next') {
          e.preventDefault();
          this.goNext();
        }
        if (action === 'back') {
          e.preventDefault();
          this.goBack();
        }
      });

      this.form.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA' && this.currentIdx < this.steps.length - 2) {
          e.preventDefault();
          this.goNext();
        }
      });

      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.submit();
      });
    }

    getFormData() {
      return new FormData(this.form);
    }

    isStepVisible(step) {
      const showIf = step.dataset.showIf;
      if (!showIf) return true;
      let rule;
      try {
        rule = JSON.parse(showIf);
      } catch (err) {
        return true;
      }
      if (!rule.field) return true;
      const data = this.getFormData();
      const val = data.get(rule.field);
      if (!val) return true;
      if (Array.isArray(rule.values_in) && rule.values_in.length) {
        return rule.values_in.includes(val);
      }
      return true;
    }

    getVisibleSteps() {
      return this.steps.filter((s) => this.isStepVisible(s));
    }

    getVisibleQuestionSteps(visible) {
      const list = visible || this.getVisibleSteps();
      return list.filter((s) => {
        const i = this.steps.indexOf(s);
        return i !== 0 && i !== this.steps.length - 1;
      });
    }

    showStep(idx) {
      this.steps.forEach((s) => s.classList.remove('is-active'));
      this.steps[idx].classList.add('is-active');
      this.currentIdx = idx;

      const isWelcome = idx === 0;
      const isThanks = idx === this.steps.length - 1;

      const visible = this.getVisibleSteps();
      const visibleQuestions = this.getVisibleQuestionSteps(visible);
      const totalQuestions = visibleQuestions.length;
      const questionIdx = isWelcome
        ? 0
        : isThanks
        ? totalQuestions
        : visibleQuestions.indexOf(this.steps[idx]) + 1;

      const pct = isThanks ? 100 : isWelcome ? 0 : (questionIdx / totalQuestions) * 100;
      if (this.progressBar) this.progressBar.style.width = pct + '%';
      if (this.stepCurrentEl) this.stepCurrentEl.textContent = questionIdx;
      if (this.stepTotalEl) this.stepTotalEl.textContent = totalQuestions;
      if (this.stepLabelEl) {
        this.stepLabelEl.textContent = isWelcome
          ? this.config.labels?.welcome || 'Welcome'
          : isThanks
          ? this.config.labels?.complete || 'Complete'
          : (this.config.labels?.question || 'Question') + ' ' + questionIdx;
      }

      const counter = this.steps[idx].querySelector('.partner-form__step-counter');
      if (counter && !isWelcome && !isThanks) {
        const questionWord = this.config.labels?.question || 'Question';
        const ofWord = this.config.labels?.of || 'of';
        counter.textContent = questionWord + ' ' + questionIdx + ' ' + ofWord + ' ' + totalQuestions;
      }

      const firstInput = this.steps[idx].querySelector('input, select, textarea');
      if (firstInput) {
        setTimeout(() => firstInput.focus(), 200);
      }
    }

    validateStep(idx) {
      const step = this.steps[idx];
      const errorEl = step.querySelector('[data-error]');
      if (errorEl) errorEl.textContent = '';

      const required = step.querySelectorAll('[required]');
      for (const field of required) {
        if (field.type === 'radio') {
          const group = step.querySelectorAll('input[name="' + field.name + '"]');
          const checked = Array.from(group).some((r) => r.checked);
          if (!checked) {
            if (errorEl) errorEl.textContent = this.config.errors?.required || 'Please pick an option to continue.';
            return false;
          }
        } else if (field.type === 'checkbox') {
          const group = step.querySelectorAll('input[name="' + field.name + '"]');
          const checked = Array.from(group).some((r) => r.checked);
          if (!checked) {
            if (errorEl) errorEl.textContent = this.config.errors?.required || 'Pick at least one option.';
            return false;
          }
        } else if (!field.value.trim()) {
          if (errorEl) errorEl.textContent = this.config.errors?.required || 'This field is required.';
          field.focus();
          return false;
        } else if (field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(field.value)) {
          if (errorEl) errorEl.textContent = this.config.errors?.email || 'Please enter a valid email address.';
          field.focus();
          return false;
        } else if (field.type === 'url' && !this.isValidUrl(field.value)) {
          if (errorEl) errorEl.textContent = this.config.errors?.url || 'Please enter a valid URL.';
          field.focus();
          return false;
        }
      }

      const checkboxRequired = step.querySelector('[data-checkbox-required]');
      if (checkboxRequired) {
        const name = checkboxRequired.dataset.checkboxRequired;
        const checked = step.querySelectorAll('input[name="' + name + '"]:checked');
        if (checked.length === 0) {
          if (errorEl) errorEl.textContent = this.config.errors?.required || 'Pick at least one option.';
          return false;
        }
      }

      return true;
    }

    isValidUrl(value) {
      const v = value.trim();
      if (!v) return false;
      try {
        const withProtocol = /^https?:\/\//i.test(v) ? v : 'https://' + v;
        new URL(withProtocol);
        return true;
      } catch (err) {
        return false;
      }
    }

    goNext() {
      if (!this.validateStep(this.currentIdx)) return;
      const visible = this.getVisibleSteps();
      const currentInVisible = visible.indexOf(this.steps[this.currentIdx]);
      const nextStepEl = visible[currentInVisible + 1];
      if (nextStepEl) {
        const nextIdx = this.steps.indexOf(nextStepEl);
        this.visitedHistory.push(nextIdx);
        this.showStep(nextIdx);
      }
    }

    goBack() {
      this.visitedHistory.pop();
      const prevIdx = this.visitedHistory[this.visitedHistory.length - 1] ?? 0;
      this.showStep(prevIdx);
    }

    getCookie(name) {
      const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
      return match ? decodeURIComponent(match[2]) : '';
    }

    async submit() {
      if (!this.validateStep(this.currentIdx)) return;

      const consentBox = this.form.querySelector('[name="legal_consent"]');
      if (consentBox && !consentBox.checked) {
        const consentError = this.form.querySelector('[data-consent-error]');
        if (consentError) {
          consentError.textContent = this.config.errors?.required || 'Please accept the terms to continue.';
        }
        return;
      }

      this.submitBtn.disabled = true;
      const originalLabel = this.submitBtn.textContent;
      this.submitBtn.textContent = this.config.labels?.submitting || 'Submitting...';

      const fd = this.getFormData();
      const fields = this.buildFields(fd);

      const hsContext = {
        pageUri: window.location.href,
        pageName: document.title,
        hutk: this.getCookie('hubspotutk'),
      };

      const body = new URLSearchParams();
      fields.forEach((f) => {
        if (typeof f.value === 'string' && f.value.includes(';')) {
          f.value.split(';').forEach((v) => {
            const trimmed = v.trim();
            if (trimmed) body.append(f.name, trimmed);
          });
        } else {
          body.append(f.name, f.value);
        }
      });
      body.append('hs_context', JSON.stringify(hsContext));

      try {
        const url =
          'https://forms.hubspot.com/uploads/form/v2/' +
          this.config.portal_id +
          '/' +
          this.config.form_guid;
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body.toString(),
        });
        if (!res.ok) {
          let errorDetail = '';
          try {
            const errText = await res.text();
            errorDetail = ' — ' + errText.slice(0, 200);
            console.error('HubSpot error response:', errText);
          } catch (e) {}
          throw new Error('HubSpot submit failed: ' + res.status + errorDetail);
        }

        if (window.dataLayer && typeof window.dataLayer.push === 'function') {
          window.dataLayer.push({ event: 'partner_form_submit' });
        }

        this.showStep(this.steps.length - 1);
      } catch (err) {
        this.submitBtn.disabled = false;
        this.submitBtn.textContent = originalLabel;
        const failMsg =
          this.config.errors?.submit_failed ||
          'Something went wrong. Please try again or email us.';
        const errorEl = this.steps[this.currentIdx].querySelector('[data-error]');
        if (errorEl) errorEl.textContent = failMsg;
        console.error(err);
      }
    }

    buildFields(fd) {
      const fields = [];
      const fullName = (fd.get('full_name') || '').trim();
      if (fullName) {
        const parts = fullName.split(/\s+/);
        fields.push({ name: 'firstname', value: parts[0] });
        if (parts.length > 1) {
          fields.push({ name: 'lastname', value: parts.slice(1).join(' ') });
        }
      }

      const phoneCountry = fd.get('phone_country') || '';
      const phone = fd.get('phone') || '';
      if (phone) {
        const combined = phoneCountry ? phoneCountry + ' ' + phone : phone;
        fields.push({ name: 'phone', value: combined.trim() });
      }

      const knownKeys = ['full_name', 'phone_country', 'phone', 'legal_consent', 'marketing_consent'];

      const multiValues = {};
      for (const [key, value] of fd.entries()) {
        if (knownKeys.includes(key)) continue;
        if (!value) continue;
        if (multiValues[key]) {
          multiValues[key].push(value);
        } else {
          multiValues[key] = [value];
        }
      }

      const fieldMap = this.config.field_map || {};

      Object.entries(multiValues).forEach(([key, values]) => {
        const hsName = fieldMap[key] || key;
        const joined = values.length > 1 ? values.join(';') : values[0];
        fields.push({ name: hsName, value: joined });
      });

      return fields.filter((f) => f.value !== undefined && f.value !== null && f.value !== '');
    }
  }

  function init() {
    document.querySelectorAll('.partner-form[data-section-id]').forEach((root) => {
      if (root.dataset.partnerFormInit) return;
      root.dataset.partnerFormInit = '1';
      try {
        new PartnerForm(root);
      } catch (err) {
        console.error('PartnerForm init failed:', err);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
